/**
 * API DTOs -> the view models the wave planning components render.
 *
 * The server calculates everything; these functions only reshape responses
 * and convert percent (0-100, API) to fractions (0-1, components). Weeks are
 * identified by their id; a missing value stays ``null`` (shown as "—").
 */
import type { DerivedDC } from '@/api/distributionCenters'
import type {
  WavePlanDcResponse,
  WavePlanDcRow,
  WavePlanDcTotals,
  WavePlanDetail,
  WavePlanFlag,
  WavePlanLine,
  WavePlanLinesResponse,
  WavePlanRollupGroup,
  WavePlanRollupMetrics,
  WavePlanRollupPlan,
  WavePlanRollupWeek,
  WavePlanSummary,
  WavePlanWeek,
  WaveSkuLevel,
} from '@/api/wavePlans'
import type { DcRow, DcTotals } from './dc-model'
import {
  NO_WAVE,
  weekLabel,
  type CalculatedWaveWeek,
  type PlanningPanel,
  type PlanningResult,
  type WaveWeek,
} from './model'
import type {
  RollupGroup,
  RollupMetrics,
  RollupPlan,
  RollupPlanDetails,
} from './rollup-model'
import type {
  SkuPogStatus,
  SkuType,
  SkuWaveAllocation,
  SkuWaveColumn,
  SkuWaveLine,
  SkuWaveRow,
} from './sku-model'

const fraction = (percent: number) => percent / 100
const nullableFraction = (percent: number | null | undefined) =>
  percent == null ? null : percent / 100

const SKU_TYPES: Record<WaveSkuLevel, SkuType> = {
  SINGLE: 'Single SKU',
  PARENT: 'Parent',
  CHILD: 'Child',
  REPLEN: 'Replen',
}

const eventOf = (week: {
  event_code: string | null
  fiscal_week: number
  is_wave_eligible: boolean
}) =>
  week.is_wave_eligible
    ? (week.event_code ?? weekLabel(week.fiscal_week))
    : NO_WAVE

// -----------------------------------------------------------------------------
// Overall plan
// -----------------------------------------------------------------------------

export function toTimeline(weeks: WavePlanWeek[]): WaveWeek[] {
  return weeks.map(week => ({
    id: week.id,
    week: week.fiscal_week,
    event: eventOf(week),
    date: week.receipt_date ?? week.week_start_date,
  }))
}

function toCalculatedWeeks(weeks: WavePlanWeek[]): CalculatedWaveWeek[] {
  return toTimeline(weeks).map((week, index) => {
    const source = weeks[index]
    return {
      ...week,
      historicalWave: fraction(source.ly_wave_percent),
      plannedWave: fraction(source.planned_percent),
      cumulativeWave: fraction(source.cumulative_percent),
      cumulativeDemandPercent: fraction(source.cumulative_demand_percent),
      receipts: source.receipts,
      averageReceipts: source.average_receipts,
      inventory: source.inventory,
      averageInventory: source.average_inventory,
      receiptsInStores: fraction(source.receipts_in_stores_percent),
      salesInStores: fraction(source.sales_in_stores_percent),
      variance: fraction(source.variance_percent),
      thresholdCrossed: source.threshold_crossed ?? false,
      // Null when frozen before the SKU-calculated waves existed (never the plan's).
      skuWave: nullableFraction(source.sku_percent),
      skuCumulativeWave: nullableFraction(source.sku_cumulative_percent),
    }
  })
}

/** Timeline position of the week holding a date, or -1. */
function weekIndexOf(
  weeks: Pick<WavePlanWeek, 'week_start_date'>[],
  date: string | null | undefined
): number {
  if (!date) return -1
  const target = new Date(`${date}T00:00:00Z`).getTime()
  return weeks.findIndex(item => {
    const start = new Date(`${item.week_start_date}T00:00:00Z`).getTime()
    return target >= start && target < start + 7 * 86_400_000
  })
}

export function toPlanningPanel(
  detail: WavePlanDetail,
  weeks: WavePlanWeek[]
): PlanningPanel {
  const setDate = detail.ap_header.set_reset_date
  const setIndex = weekIndexOf(weeks, setDate)
  return {
    season: detail.season_name,
    stores: detail.header.store_count,
    previousStores: null,
    skus: detail.header.sellable_count,
    previousSkus: null,
    pogSet: detail.header.pog_set,
    pogMax: detail.header.pog_max,
    purchaseUnits: detail.header.purchase_units,
    setDate,
    setIndex,
    setWeekDate: weeks[setIndex]?.planner_receipt_date ?? setDate,
    note: detail.notes,
  }
}

export function toPlanningResult(
  summary: WavePlanSummary,
  weeks: WavePlanWeek[]
): PlanningResult {
  return {
    weeks: toCalculatedWeeks(weeks),
    total: fraction(summary.total_percent),
    skuTotal: nullableFraction(summary.sku_total_percent),
    minimumFirstWave: fraction(summary.minimum_first_wave_percent),
    firstWave: fraction(summary.first_wave_percent),
    // Results frozen before 1.5.0 have no plan/SKU split: their first wave is the plan's.
    skuFirstWave:
      summary.planned_first_wave_percent == null
        ? null
        : fraction(summary.first_wave_percent),
    plannedFirstWave: fraction(
      summary.planned_first_wave_percent ?? summary.first_wave_percent
    ),
    pogSetMinimum: nullableFraction(summary.pog_set_minimum_percent),
    belowPogSetCount: summary.below_pog_set_count ?? 0,
    skuBasis: (summary.metrics_basis ?? 'plan_default') === 'sku_bottoms_up',
    inSeasonDemandPercent: fraction(summary.in_season_demand_percent),
    averageStoreReceipts: summary.average_store_receipts,
    averageStoreSales: summary.average_store_sales,
  }
}

// -----------------------------------------------------------------------------
// SKU detail
// -----------------------------------------------------------------------------

export function toSkuLine(line: WavePlanLine): SkuWaveLine {
  const stores = line.store_count
  return {
    id: line.line_ref_id,
    // A new SKU has only the AP's plan SKU # until its SKU number exists.
    sku: line.sku_number?.trim() ? line.sku_number : (line.plan_sku_num ?? ''),
    description: line.description ?? '',
    type: SKU_TYPES[line.sku_level],
    vendor: line.vendor_name ?? '',
    stores,
    cp: line.case_pack,
    ip: line.inner_pack,
    cube: line.case_pack_cube,
    cost: line.unit_cost,
    retail: line.unit_retail,
    childPurchaseUnits: null,
    purchaseQty: line.base_quantity,
    moq: line.moq,
    pogSet: line.pog_set_qty,
    pogMax: line.pog_max_qty,
    totalQty: line.total_qty,
    totalInnerPacks: line.inner_pack ? line.total_qty / line.inner_pack : null,
    avgStoreUnits: stores > 0 ? line.total_qty / stores : null,
    reviewNote:
      line.flags
        .filter(flag => flag.severity !== 'INFO')
        .map(flag => flag.message)
        .join(' ') || null,
    pog: toSkuPog(line.flags),
    belowMoq:
      line.flags
        .filter(flag => flag.code === 'BELOW_MOQ')
        .map(flag => flag.message)
        .join(' ') || null,
    noStoreModel:
      line.flags.find(flag => flag.code === 'NO_STORE_MODEL')?.message ?? null,
  }
}

const POG_FLAGS: [string, SkuPogStatus][] = [
  ['FIRST_WAVE_BELOW_POG_SET', 'short'],
  ['SKU_BUY_BELOW_POG_SET', 'buyBelow'],
  ['SKU_FIRST_WAVE_RAISED', 'raised'],
]

/** The most important of the line's POG flags, with all their messages. */
function toSkuPog(flags: WavePlanFlag[]): SkuWaveLine['pog'] {
  const found = POG_FLAGS.filter(([code]) =>
    flags.some(flag => flag.code === code)
  )
  if (!found.length) return null
  return {
    status: found[0][1],
    message: flags
      .filter(flag => found.some(([code]) => code === flag.code))
      .map(flag => flag.message)
      .join(' '),
  }
}

export function toSkuColumns(response: WavePlanLinesResponse): SkuWaveColumn[] {
  return response.weeks.map(week => ({
    id: week.id,
    week: week.fiscal_week,
    event: eventOf(week),
    date: week.date ?? '',
    overallPercent: fraction(week.default_percent),
    skuPercent: nullableFraction(week.sku_percent),
    outside: week.outside,
  }))
}

const toAllocation = (cell: {
  percent: number | null
  qty: number
  avg_store: number | null
  cube_per_dc: number | null
  final_qty?: number | null
  source?: string
}): SkuWaveAllocation => ({
  percent: nullableFraction(cell.percent),
  qty: cell.qty,
  finalQty: cell.final_qty ?? null,
  avgStore: cell.avg_store,
  cubicFtPerDc: cell.cube_per_dc,
  quantityOverride: cell.source === 'OVERRIDE_QUANTITY',
})

export function toSkuRows(response: WavePlanLinesResponse): SkuWaveRow[] {
  return response.lines.map(line => ({
    id: line.line_ref_id,
    line: toSkuLine(line),
    waves: Object.fromEntries(
      line.cells.map(cell => [cell.week_id, toAllocation(cell)])
    ),
    totalQty: line.total_qty,
    allocatedQty: line.allocated_qty,
    totalPercent: fraction(line.total_percent),
    adjusted: line.has_custom_distribution,
    outsideWeeks: line.outside_week_ids,
    // A SKU whose first wave misses its POG set needs the planner (flagged, not blocking).
    needsReview:
      line.needs_review ||
      line.flags.some(flag => flag.code === 'FIRST_WAVE_BELOW_POG_SET'),
    wpQty: line.wp_final_quantity ?? null,
    varianceUnits: line.variance_units ?? null,
    variancePercent: nullableFraction(line.variance_percent),
  }))
}

export function toSkuSummary(response: WavePlanLinesResponse): SkuWaveRow {
  const totals = response.totals
  return {
    id: 'filtered-totals',
    line: null,
    waves: Object.fromEntries(
      totals.cells.map(cell => [cell.week_id, toAllocation(cell)])
    ),
    totalQty: totals.total_qty,
    allocatedQty: totals.allocated_qty,
    totalPercent: nullableFraction(totals.total_percent),
    adjusted: false,
    outsideWeeks: [],
    needsReview: totals.needs_review,
    wpQty: totals.wp_final_quantity ?? null,
    varianceUnits: totals.variance_units ?? null,
    variancePercent: nullableFraction(totals.variance_percent),
  }
}

// -----------------------------------------------------------------------------
// DC detail
// -----------------------------------------------------------------------------

export function toDcCenters(response: WavePlanDcResponse): DerivedDC[] {
  return response.centers.map(dc => ({
    id: dc.id,
    code: dc.code,
    name: dc.name,
  }))
}

export function toDcRow(row: WavePlanDcRow): DcRow {
  return {
    id: row.line_ref_id,
    line: {
      id: row.line_ref_id,
      sku: row.sku_number ?? '',
      description: row.description ?? '',
      type: SKU_TYPES[row.sku_level],
      vendor: row.vendor_name ?? '',
    },
    waveQty: row.wave_qty,
    distributionQty: row.distribution_qty,
    moqDelta: row.moq_delta,
    logistics: { multiple: row.multiple, moq: row.moq },
    belowMoq: row.below_moq ?? false,
    // Undefined when the response has no routing (null: unknown).
    conveyable: row.conveyable,
    routingDefaulted: row.routing_defaulted ?? false,
    noStoreModel: row.no_store_model ?? false,
    plannerSplit:
      row.has_override ||
      row.cells.some(cell => cell.source.startsWith('OVERRIDE')),
    allocations: Object.fromEntries([
      // DCs serving the SKU without a share (e.g. Core routing sent the wave to
      // a single DC) stay editable at 0%, so the planner can change the DC count.
      ...row.eligible_dc_ids.map(dcId => [
        dcId,
        { percent: 0, raw: 0, adjusted: 0, delta: 0, poCost: 0 },
      ]),
      ...row.cells.map(cell => [
        cell.dc_id,
        {
          percent: cell.percent,
          raw: cell.raw,
          adjusted: cell.adjusted,
          delta: cell.delta,
          poCost: cell.po_cost ?? null,
        },
      ]),
    ]),
    percent: row.percent,
    raw: row.raw,
    adjusted: row.adjusted,
    delta: row.delta,
    balance: row.balance,
    balanced: row.balanced,
    sourceNeedsReview: row.source_needs_review,
    otherWeekIds: row.other_week_ids,
    storeModel: row.store_model_id
      ? { id: row.store_model_id, name: row.store_model_name ?? '' }
      : null,
    // The same key as the API's vendor totals: id, else code, else name.
    vendor: {
      key: row.vendor_id || row.vendor_code || row.vendor_name || '',
      name: row.vendor_name || row.vendor_code || '',
    },
    unitCost: row.unit_cost ?? null,
    poCost: row.po_cost ?? null,
  }
}

export function toDcTotals(totals: WavePlanDcTotals): DcTotals {
  return {
    waveQty: totals.wave_qty,
    raw: totals.raw,
    adjusted: totals.adjusted,
    delta: totals.delta,
    balance: totals.balance,
    excess: totals.excess,
    excessCount: totals.excess_count,
    excessPercent: totals.excess_percent,
    centers: totals.centers,
  }
}

// -----------------------------------------------------------------------------
// Roll-up
// -----------------------------------------------------------------------------

const toMetrics = (metrics: WavePlanRollupMetrics): RollupMetrics => ({
  percent: nullableFraction(metrics.percent),
  units: metrics.units,
  skuCount: metrics.sku_count,
  repackSkuCount: metrics.repack_sku_count,
  cube: metrics.cube,
})

const EMPTY_ROLLUP_METRICS: RollupMetrics = {
  percent: null,
  units: 0,
  skuCount: null,
  repackSkuCount: null,
  cube: null,
}

/**
 * Numeric week id unique across fiscal years (e.g. 202618), so a group whose
 * horizon crosses a year boundary never merges two different weeks.
 */
const rollupWeekId = (week: { fiscal_year: number; fiscal_week: number }) =>
  week.fiscal_year * 100 + week.fiscal_week

/** Every group week for every plan; weeks outside a plan's horizon are empty. */
const denseWaves = (
  weeks: WavePlanRollupWeek[],
  source: WavePlanRollupWeek[]
): Record<number, RollupMetrics> => {
  const byKey = new Map(source.map(week => [week.week_key, toMetrics(week)]))
  return Object.fromEntries(
    weeks.map(week => [
      rollupWeekId(week),
      byKey.get(week.week_key) ?? EMPTY_ROLLUP_METRICS,
    ])
  )
}

/** POG description, location, CICO and repack ship quantity have no source yet. */
const toRollupDetails = (plan: WavePlanRollupPlan): RollupPlanDetails => {
  const header = plan.header
  return {
    hierarchyCode: null,
    description: plan.name,
    pogDescription: null,
    location: null,
    stores: header?.store_count ?? null,
    sellableCount: header?.sellable_count ?? null,
    shippingCount: header?.shipping_count ?? null,
    piabCount: header?.piab_count ?? null,
    cicoCount: null,
    repackCount: header?.repack_count ?? null,
    repackShipQty: null,
  }
}

export function toRollupGroups(groups: WavePlanRollupGroup[]): RollupGroup[] {
  return groups.map(group => {
    const weeks: WaveWeek[] = group.weeks.map(week => ({
      id: week.week_key,
      week: rollupWeekId(week),
      event: week.event_code ?? weekLabel(week.fiscal_week),
      date: week.date ?? '',
    }))
    const idByKey = new Map(
      group.weeks.map(week => [week.week_key, rollupWeekId(week)])
    )
    const calendar = {
      id: String(group.fiscal_year),
      label: `FY${group.fiscal_year}`,
      year: group.fiscal_year,
    }
    const plans: RollupPlan[] = group.plans.map(plan => ({
      plan: {
        id: plan.wave_plan_id,
        name: plan.name,
        status: plan.status,
        calendarId: calendar.id,
      },
      details: toRollupDetails(plan),
      setDate: plan.set_reset_date ?? '',
      baseUnits: plan.base_units,
      waves: denseWaves(group.weeks, plan.weeks),
      season: toMetrics(plan.season),
      activeWeeks: plan.active_week_keys
        .map(key => idByKey.get(key))
        .filter((value): value is number => value !== undefined),
      waveCount: plan.wave_count,
      needsReview: plan.needs_review,
    }))
    const active = new Set(group.active_week_keys)
    return {
      calendar,
      plans,
      weeks,
      activeWeeks: weeks.filter(week => active.has(week.id)),
      waves: denseWaves(group.weeks, group.weeks),
      baseUnits: group.base_units,
      season: toMetrics(group.season),
    }
  })
}
